/**
 * Product 360° Viewer
 * A world-class, accessible, performant 360° product rotation viewer
 *
 * Features:
 * - Smooth momentum scrolling with physics-based deceleration
 * - 60fps updates via RequestAnimationFrame
 * - Full keyboard accessibility (WCAG 2.1 AA)
 * - Smart preloading strategies
 * - Error handling and recovery
 * - Touch gesture support
 * - Visual progress indicator
 */

(function() {
  'use strict';

  // Default configuration
  const DEFAULT_CONFIG = {
    // Interaction
    scrubThreshold: 8,
    sensitivity: 1.5,
    reverseDirection: true,

    // Momentum (disabled by default - video seeking can be stuttery with momentum)
    enableMomentum: false,
    friction: 0.92,
    minVelocity: 0.5,

    // Performance
    updateInterval: 16,
    preloadStrategy: 'visible',

    // Accessibility
    keyboardStep: 0.05,
    announceChanges: true,

    // Visual
    showProgressBar: true,
    showHint: true,
    hintDismissAfter: 5000,

    // Timeouts
    loadTimeout: 15000
  };

  /**
   * Momentum physics tracker
   */
  class MomentumTracker {
    constructor(friction = 0.92, minVelocity = 0.5) {
      this.friction = friction;
      this.minVelocity = minVelocity;
      this.positions = [];
      this.animationId = null;
    }

    addPosition(x, timestamp) {
      this.positions.push({ x, timestamp });
      const cutoff = timestamp - 100;
      this.positions = this.positions.filter(p => p.timestamp > cutoff);
    }

    calculateVelocity() {
      if (this.positions.length < 2) return 0;
      const first = this.positions[0];
      const last = this.positions[this.positions.length - 1];
      const dt = last.timestamp - first.timestamp;
      return dt > 0 ? (last.x - first.x) / dt * 16 : 0;
    }

    startMomentum(onUpdate, onComplete) {
      let velocity = this.calculateVelocity();
      this.positions = [];

      const animate = () => {
        velocity *= this.friction;

        if (Math.abs(velocity) < this.minVelocity) {
          this.animationId = null;
          onComplete();
          return;
        }

        onUpdate(velocity);
        this.animationId = requestAnimationFrame(animate);
      };

      this.animationId = requestAnimationFrame(animate);
    }

    stop() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      this.positions = [];
    }
  }

  /**
   * Throttled video time controller - simplified for smoother scrubbing
   */
  class VideoTimeController {
    constructor(video, minInterval = 16) {
      this.video = video;
      this.minInterval = minInterval;
      this.lastUpdate = 0;
      this.targetTime = 0;
      this.rafId = null;
    }

    setTime(time) {
      this.targetTime = time;

      // Use RAF for smooth updates, but don't queue multiple
      if (!this.rafId) {
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          if (!Number.isNaN(this.targetTime) && Number.isFinite(this.targetTime)) {
            this.video.currentTime = this.targetTime;
          }
        });
      }
    }

    destroy() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    }
  }

  /**
   * Main Product 360 Viewer Class
   */
  class Product360Viewer {
    static instances = new Map();
    static instanceId = 0;

    constructor(container, options = {}) {
      this.id = ++Product360Viewer.instanceId;
      this.container = container;
      this.config = { ...DEFAULT_CONFIG, ...this.parseDataAttributes(), ...options };

      this.video = null;
      this.mediaContainer = null;
      this.progressBar = null;
      this.progressFill = null;
      this.liveRegion = null;
      this.hint = null;

      this.isScrubbing = false;
      this.isReady = false;
      this.pointerStartX = 0;
      this.lastPointerX = 0;
      this.scrubStartTime = 0;
      this.activePointerId = null;
      this.currentPosition = 0; // Track position ourselves for precise wrapping
      this.touchCount = 0; // Track number of active touches for multi-touch detection

      this.momentum = new MomentumTracker(this.config.friction, this.config.minVelocity);
      this.timeController = null;
      this.abortController = new AbortController();

      this.flickityInstance = null;

      Product360Viewer.instances.set(this.id, this);
      this.init();
    }

    parseDataAttributes() {
      const config = {};
      const dataset = this.container.dataset;

      if (dataset['360Sensitivity']) config.sensitivity = parseFloat(dataset['360Sensitivity']);
      if (dataset['360Momentum'] === 'false') config.enableMomentum = false;
      if (dataset['360Preload']) config.preloadStrategy = dataset['360Preload'];
      if (dataset['360Reverse'] === 'false') config.reverseDirection = false;
      if (dataset['360Progress'] === 'false') config.showProgressBar = false;
      if (dataset['360Hint'] === 'false') config.showHint = false;

      return config;
    }

    init() {
      try {
        this.video = this.container.querySelector('video.video-360');
        this.mediaContainer = this.container.closest('.product__media') || this.container;

        if (!this.video) {
          console.warn('Product360Viewer: No video-360 element found');
          return;
        }

        this.setupAccessibility();
        this.setupProgressBar();
        this.setupLiveRegion();
        this.initPreloading();

      } catch (error) {
        console.error('Product360Viewer: Initialization failed', error);
        this.showError();
      }
    }

    setupAccessibility() {
      this.container.setAttribute('tabindex', '0');
      this.container.setAttribute('role', 'slider');
      this.container.setAttribute('aria-label', 'Product 360 degree view. Use left and right arrow keys to rotate, or drag to rotate.');
      this.container.setAttribute('aria-valuemin', '0');
      this.container.setAttribute('aria-valuemax', '100');
      this.container.setAttribute('aria-valuenow', '0');
      this.container.classList.add('product-360-viewer');

      const signal = this.abortController.signal;
      this.container.addEventListener('keydown', this.handleKeyboard.bind(this), { signal });
    }

    setupProgressBar() {
      if (!this.config.showProgressBar) return;

      const existing = this.container.querySelector('.viewer-360-progress');
      if (existing) {
        this.progressBar = existing;
        this.progressFill = existing.querySelector('.viewer-360-progress-fill');
        return;
      }

      this.progressBar = document.createElement('div');
      this.progressBar.className = 'viewer-360-progress';
      this.progressBar.innerHTML = `
        <div class="viewer-360-progress-track">
          <div class="viewer-360-progress-fill"></div>
        </div>
      `;
      this.container.appendChild(this.progressBar);
      this.progressFill = this.progressBar.querySelector('.viewer-360-progress-fill');
    }

    setupLiveRegion() {
      if (!this.config.announceChanges) return;

      this.liveRegion = document.createElement('div');
      this.liveRegion.className = 'sr-only';
      this.liveRegion.setAttribute('aria-live', 'polite');
      this.liveRegion.setAttribute('aria-atomic', 'true');
      this.container.appendChild(this.liveRegion);
    }

    initPreloading() {
      const strategy = this.config.preloadStrategy;

      switch (strategy) {
        case 'immediate':
          this.loadVideo();
          break;
        case 'interaction':
          this.setupInteractionPreload();
          break;
        case 'visible':
        default:
          this.setupVisibilityPreload();
          break;
      }
    }

    setupVisibilityPreload() {
      this.video.preload = 'metadata';

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadVideo();
            observer.disconnect();
          }
        });
      }, { threshold: 0.1 });

      observer.observe(this.container);
    }

    setupInteractionPreload() {
      this.video.preload = 'metadata';
      const signal = this.abortController.signal;

      const loadOnInteract = () => {
        this.loadVideo();
      };

      this.container.addEventListener('pointerenter', loadOnInteract, { once: true, signal });
      this.container.addEventListener('focus', loadOnInteract, { once: true, signal });
    }

    loadVideo() {
      if (this.isReady) return;

      this.video.preload = 'auto';
      this.container.classList.add('is-loading');

      const timeout = setTimeout(() => {
        if (!this.isReady) {
          console.warn('Product360Viewer: Video load timeout');
          this.container.classList.remove('is-loading');
          this.container.classList.add('load-timeout');
        }
      }, this.config.loadTimeout);

      const signal = this.abortController.signal;

      this.video.addEventListener('loadedmetadata', () => {
        clearTimeout(timeout);
        this.onVideoReady();
      }, { once: true, signal });

      this.video.addEventListener('error', (e) => {
        clearTimeout(timeout);
        console.error('Product360Viewer: Video load error', e);
        this.showError();
      }, { once: true, signal });

      this.video.addEventListener('progress', () => {
        this.updateLoadProgress();
      }, { signal });

      this.video.load();
    }

    updateLoadProgress() {
      if (this.video.buffered.length > 0 && this.video.duration) {
        const percent = (this.video.buffered.end(0) / this.video.duration) * 100;
        this.container.style.setProperty('--load-progress', `${percent}%`);
      }
    }

    onVideoReady() {
      this.isReady = true;
      this.video.currentTime = 0;
      this.currentPosition = 0;

      // Enable looping for continuous 360 rotation
      this.video.loop = true;

      this.timeController = new VideoTimeController(this.video, this.config.updateInterval);

      this.container.classList.remove('is-loading');
      this.container.classList.add('video-ready');
      this.mediaContainer.classList.add('video-ready');

      this.setupPointerEvents();
      this.setupTouchGestures();
      this.setupVideoEvents();
      this.setupFlickityIntegration();
      this.setupHint();

      // Start auto-play (muted videos can auto-play on all browsers)
      this.video.play().catch(err => {
        // Auto-play may be blocked by browser - that's ok, user can still scrub
        console.log('360 viewer: Auto-play blocked, user can still drag to rotate');
      });

      this.emit('ready');
    }

    setupPointerEvents() {
      const signal = this.abortController.signal;

      this.video.addEventListener('pointerdown', this.handlePointerDown.bind(this), { signal });
      this.video.addEventListener('pointermove', this.handlePointerMove.bind(this), { signal });
      this.video.addEventListener('pointerup', this.handlePointerUp.bind(this), { signal });
      this.video.addEventListener('pointercancel', this.handlePointerUp.bind(this), { signal });
      this.video.addEventListener('lostpointercapture', this.handlePointerUp.bind(this), { signal });
    }

    setupTouchGestures() {
      const signal = this.abortController.signal;

      // Track touch start - add is-touching class immediately to set touch-action: none
      this.video.addEventListener('touchstart', (e) => {
        this.touchCount = e.touches.length;

        // Ignore multi-touch (pinch gestures)
        if (e.touches.length > 1) {
          this.cancelScrubbing();
          return;
        }

        // Add touching class to prevent scroll via CSS touch-action
        this.container.classList.add('is-touching');

        // Prevent default to stop iOS from doing anything weird
        e.preventDefault();
      }, { passive: false, signal });

      // Track additional touches (pinch started mid-scrub)
      this.video.addEventListener('touchmove', (e) => {
        // If multi-touch detected, cancel scrubbing
        if (e.touches.length > 1) {
          this.cancelScrubbing();
          return;
        }

        // Prevent scroll while touching the video
        if (this.container.classList.contains('is-touching')) {
          e.preventDefault();
        }
      }, { passive: false, signal });

      // Clean up on touch end
      this.video.addEventListener('touchend', (e) => {
        this.touchCount = e.touches.length;
        if (e.touches.length === 0) {
          this.container.classList.remove('is-touching');
        }
      }, { signal });

      this.video.addEventListener('touchcancel', () => {
        this.touchCount = 0;
        this.container.classList.remove('is-touching');
        this.cancelScrubbing();
      }, { signal });

      // Prevent double-tap zoom on container
      let lastTap = 0;
      this.container.addEventListener('touchend', (e) => {
        const now = Date.now();
        if (now - lastTap < 300) {
          e.preventDefault();
        }
        lastTap = now;
      }, { signal });
    }

    cancelScrubbing() {
      if (this.isScrubbing) {
        this.isScrubbing = false;
        this.video.classList.remove('is-scrubbing');
        this.container.classList.remove('is-scrubbing');
        this.mediaContainer.classList.remove('is-scrubbing');
      }
      this.activePointerId = null;
      this.enableFlickity();
    }

    setupVideoEvents() {
      const signal = this.abortController.signal;

      // Track when video is playing for UI state
      this.video.addEventListener('play', () => {
        this.container.classList.add('is-playing');
        this.dismissHint();
      }, { signal });

      this.video.addEventListener('pause', () => {
        this.container.classList.remove('is-playing');
      }, { signal });
    }

    setupFlickityIntegration() {
      const sliderElement = this.mediaContainer.closest('[data-slideshow]');
      if (sliderElement && typeof Flickity !== 'undefined') {
        this.flickityInstance = Flickity.data(sliderElement);
      }
    }

    setupHint() {
      if (!this.config.showHint) return;

      this.hint = this.container.querySelector('.drag-hint');
      if (!this.hint) return;

      const hintKey = 'product-360-hint-seen';
      if (localStorage.getItem(hintKey)) {
        this.hint.classList.add('dismissed');
        return;
      }

      // Auto-dismiss after timeout
      setTimeout(() => {
        this.dismissHint();
      }, this.config.hintDismissAfter);

      // Dismiss on first interaction
      const signal = this.abortController.signal;
      this.container.addEventListener('pointerdown', () => {
        this.dismissHint();
        localStorage.setItem(hintKey, 'true');
      }, { once: true, signal });
    }

    dismissHint() {
      if (this.hint) {
        this.hint.classList.add('dismissed');
      }
    }

    handleKeyboard(e) {
      if (!this.isReady) return;

      const step = this.config.keyboardStep * (this.video.duration || 0);

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          this.seekRelative(-step);
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          this.seekRelative(step);
          break;
        case 'Home':
          e.preventDefault();
          this.seekTo(0);
          break;
        case 'End':
          e.preventDefault();
          this.seekTo(this.video.duration);
          break;
      }
    }

    handlePointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!this.isReady) return;

      // Ignore if multi-touch is active
      if (this.touchCount > 1) return;

      this.momentum.stop();
      this.activePointerId = e.pointerId;
      this.pointerStartX = e.clientX;
      this.scrubStartTime = this.video.currentTime || 0;

      try {
        this.video.setPointerCapture(e.pointerId);
      } catch (err) {
        // Ignore capture errors
      }

      this.disableFlickity();
    }

    handlePointerMove(e) {
      if (this.activePointerId !== e.pointerId) return;
      if (!this.isReady) return;

      // Cancel if multi-touch started
      if (this.touchCount > 1) {
        this.cancelScrubbing();
        return;
      }

      const deltaX = e.clientX - this.pointerStartX;

      // Check threshold before starting scrub
      if (!this.isScrubbing && Math.abs(deltaX) < this.config.scrubThreshold) {
        return;
      }

      if (!this.isScrubbing) {
        this.beginScrubbing();
        this.lastPointerX = e.clientX;
      }

      // Track for momentum
      this.momentum.addPosition(e.clientX, performance.now());

      // Calculate incremental movement for smooth continuous rotation
      this.updateVideoTimeIncremental(e.clientX);
      this.lastPointerX = e.clientX;
    }

    handlePointerUp(e) {
      if (this.activePointerId !== e.pointerId) return;

      try {
        this.video.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Ignore release errors
      }

      this.activePointerId = null;
      this.container.classList.remove('is-touching');

      if (this.isScrubbing) {
        this.endScrubbing();
      }

      this.enableFlickity();
    }

    beginScrubbing() {
      this.isScrubbing = true;
      this.video.pause();
      // Sync our tracked position with video's actual position when starting
      this.currentPosition = this.video.currentTime || 0;
      this.video.classList.add('is-scrubbing');
      this.container.classList.add('is-scrubbing');
      this.mediaContainer.classList.add('is-scrubbing');
      this.dismissHint();
      this.emit('scrubstart', { position: this.currentPosition });
    }

    endScrubbing() {
      this.isScrubbing = false;
      this.video.classList.remove('is-scrubbing');
      this.container.classList.remove('is-scrubbing');
      this.mediaContainer.classList.remove('is-scrubbing');

      // Apply momentum if enabled
      if (this.config.enableMomentum) {
        this.applyMomentum();
      }

      this.emit('scrubend', { position: this.video.currentTime });
    }

    applyMomentum() {
      this.container.classList.add('has-momentum');

      this.momentum.startMomentum(
        (velocity) => {
          const duration = this.video.duration || 1;
          const containerWidth = this.container.clientWidth || 1;
          const direction = this.config.reverseDirection ? -1 : 1;
          const delta = (velocity / containerWidth) * duration * this.config.sensitivity * direction;

          let newTime = this.video.currentTime + delta;

          // Wrap around for continuous rotation
          if (newTime < 0) newTime = duration + newTime;
          if (newTime > duration) newTime = newTime - duration;

          this.timeController.setTime(newTime);
          this.updateProgress();
        },
        () => {
          this.container.classList.remove('has-momentum');
        }
      );
    }

    updateVideoTimeIncremental(clientX) {
      const duration = this.video.duration;
      if (!duration) return;

      const containerWidth = this.container.clientWidth || 1;
      const deltaX = clientX - (this.lastPointerX || clientX);
      const direction = this.config.reverseDirection ? -1 : 1;

      // Incremental change based on small movement
      const timeChange = (deltaX / containerWidth) * duration * this.config.sensitivity * direction;

      // Use our tracked position (not video.currentTime which can be imprecise)
      let newTime = this.currentPosition + timeChange;

      // Wrap around for continuous 360° rotation using modulo
      newTime = ((newTime % duration) + duration) % duration;

      // Update our tracked position and sync to video
      this.currentPosition = newTime;
      this.timeController.setTime(newTime);
      this.updateProgress();
      this.updateAriaValue();
    }

    updateProgress() {
      if (!this.progressFill || !this.video.duration) return;
      const percent = (this.currentPosition / this.video.duration) * 100;
      this.progressFill.style.width = `${percent}%`;
    }

    updateAriaValue() {
      if (!this.video.duration) return;
      const percent = Math.round((this.currentPosition / this.video.duration) * 100);
      this.container.setAttribute('aria-valuenow', percent);
    }

    seekRelative(delta) {
      if (!this.isReady) return;

      const duration = this.video.duration || 1;
      let newTime = this.currentPosition + delta;

      // Wrap around using modulo
      newTime = ((newTime % duration) + duration) % duration;

      this.seekTo(newTime);
    }

    seekTo(time) {
      if (!this.isReady) return;

      const duration = this.video.duration || 1;
      // Wrap instead of clamp for 360
      let newTime = ((time % duration) + duration) % duration;

      this.currentPosition = newTime;
      this.video.currentTime = newTime;
      this.updateProgress();
      this.updateAriaValue();
      this.announcePosition();
    }

    announcePosition() {
      if (!this.config.announceChanges || !this.liveRegion) return;
      const percent = Math.round((this.currentPosition / this.video.duration) * 100);
      this.liveRegion.textContent = `Viewing angle: ${percent} percent`;
    }

    disableFlickity() {
      if (this.flickityInstance) {
        this.flickityInstance.options.draggable = false;
        this.flickityInstance.updateDraggable();
      }
    }

    enableFlickity() {
      if (this.flickityInstance) {
        this.flickityInstance.options.draggable = true;
        this.flickityInstance.updateDraggable();
      }
    }

    showError() {
      this.container.classList.add('viewer-360-error');
    }

    emit(eventName, detail = {}) {
      this.container.dispatchEvent(new CustomEvent(`product360:${eventName}`, {
        detail: { ...detail, viewer: this },
        bubbles: true
      }));
    }

    destroy() {
      this.momentum.stop();

      if (this.timeController) {
        this.timeController.destroy();
      }

      this.abortController.abort();

      this.video = null;
      this.container = null;
      this.mediaContainer = null;

      Product360Viewer.instances.delete(this.id);
    }
  }

  /**
   * Auto-initialize viewers on DOMContentLoaded
   */
  function initProduct360Viewers() {
    document.querySelectorAll('deferred-media[data-360-viewer]').forEach(container => {
      if (!container.product360Viewer) {
        container.product360Viewer = new Product360Viewer(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProduct360Viewers);
  } else {
    initProduct360Viewers();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', initProduct360Viewers);

  // Expose to global scope
  window.Product360Viewer = Product360Viewer;
  window.initProduct360Viewers = initProduct360Viewers;

})();
